import { createFileRoute, Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { ListChecks, Send, ClipboardList, Truck, ArrowRight } from "lucide-react";
import { pageMeta, jsonLdScriptTag } from "@/lib/seo";

const STEPS = [
  { icon: ListChecks, n: "01", t: "Eliges paquete o servicios", d: "Empieza con un paquete o pide los servicios que necesitas por separado." },
  { icon: Send,       n: "02", t: "Revisas y pagas tu pedido", d: "Pago seguro online con Stripe. Te confirmamos por WhatsApp o email los siguientes pasos." },
  { icon: ClipboardList, n: "03", t: "Llenas tu formulario", d: "Recogemos los datos clave de tu compañía y de tu operación." },
  { icon: Truck,      n: "04", t: "Empezamos tu proceso",    d: "Hacemos los trámites con orden y te mantenemos al tanto en cada etapa." },
];

const HOW_TO_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "Cómo lanzar tu trucking company con Truck Company Center",
  step: STEPS.map((s, i) => ({
    "@type": "HowToStep",
    position: i + 1,
    name: s.t,
    text: s.d,
  })),
};

export const Route = createFileRoute("/como-funciona")({
  head: () => {
    const m = pageMeta({
      path: "/como-funciona",
      title: "Cómo funciona paso a paso | Truck Company Center",
      description:
        "Cuatro pasos: eliges paquete o servicios, pagas online, llenas tu formulario y empezamos los trámites de LLC, EIN, USDOT, MC, Central Dispatch y Super Dispatch.",
      ogTitle: "Cómo funciona — Truck Company Center",
      ogDescription:
        "Un proceso claro para abrir tu trucking company de car carrier.",
    });
    return {
      meta: m.meta,
      links: m.links,
      scripts: [
        { type: "application/ld+json", children: jsonLdScriptTag(HOW_TO_JSON_LD) },
      ],
    };
  },
  component: ComoFunciona,
});

function ComoFunciona() {
  return (
    <div>
      <section className="border-b border-hairline bg-radial-fade">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="text-xs uppercase tracking-[0.2em] text-label-accent">Proceso</div>
          <h1 className="mt-3 font-display text-4xl font-semibold text-foreground md:text-5xl">Así funciona</h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">Cuatro pasos. Sin trabas. Sin llamadas previas obligatorias.</p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-4 py-14 md:px-6 md:py-20">
        <ol className="space-y-4">
          {STEPS.map((s) => (
            <li key={s.n} className="rounded-lg border border-hairline bg-surface p-6 md:flex md:items-start md:gap-6 md:p-8">
              <div className="flex items-center gap-3 md:flex-col md:items-start md:gap-2">
                <div className="grid h-12 w-12 place-items-center rounded-md bg-primary/15 text-primary">
                  <s.icon className="h-6 w-6" />
                </div>
                <div className="font-mono text-sm tracking-widest text-primary md:mt-2">{s.n}</div>
              </div>
              <div className="mt-4 md:mt-0">
                <h2 className="font-display text-xl font-semibold text-foreground">{s.t}</h2>
                <p className="mt-2 text-muted-foreground">{s.d}</p>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-12 flex flex-wrap items-center justify-center gap-3">
          <Link to="/paquetes">
            <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
              Ver paquetes <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <Link to="/servicios">
            <Button size="lg" variant="outline" className="border-hairline bg-surface hover:bg-surface-2">
              Elegir lo que necesito
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
